import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { browserHistory } from 'react-router';

import { getReleaseGroupBaseQueryKey } from '../../../../../hooks/api/releaseGroups/useReleaseGroup';
import useAsyncValue from '../../../../../hooks/useAsyncValue';
import useRequestConnectedToAsyncValue from '../../../../../hooks/useRequestConnectedToAsyncValue';
import { put } from '../../../../../redux/api';
import type { UpdateProjectGroupReleaseRequest } from '../../../../../../typings/api';
import type { ProjectGroupRelease } from '../../../../../types';

const updateRelease = async (groupId: string, releaseId: string, req: UpdateProjectGroupReleaseRequest) => {
  return put<UpdateProjectGroupReleaseRequest, ProjectGroupRelease>(`/api/project_group/${groupId}/release/${releaseId}`, req);
};

/**
 * useUpdateRelease is a hook for updating existing releases.
 */
function useUpdateRelease() {
  const asyncValue = useAsyncValue<ProjectGroupRelease>();
  //  TODO: looking to improve the typing for `request` in useRequestConnectedToAsyncValue so we don't need to do this,
  //  but at least it's typed correctly downstream.
  const onUpdateRelease = useRequestConnectedToAsyncValue(updateRelease, asyncValue) as unknown as typeof updateRelease;

  const queryClient = useQueryClient();

  //  After a release is updated, redirect the user to the group summary page. The release group page stays mounted
  //  through the edit, so its cached release has to be refetched to show the edit.
  useEffect(() => {
    if (asyncValue.value) {
      void queryClient.invalidateQueries({ queryKey: getReleaseGroupBaseQueryKey(asyncValue.value.projectGroupId) });
      browserHistory.push(`/projects/group/${asyncValue.value.projectGroupId}`);
    }
  }, [asyncValue.value, queryClient]);

  return {
    isUpdatingRelease: asyncValue.isLoading,
    errors: asyncValue.errors,
    onResetErrors: asyncValue.empty,
    onUpdateRelease,
  };
}

export default useUpdateRelease;
